import type { AdminUser } from '@val/shared'
import { type FormEvent, useCallback, useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { PollSettingsCard } from '../admin/PollSettingsCard.tsx'
import { useAuthContext } from '../auth/AuthContext.tsx'
import { createUser, deleteUser, fetchUsers, resetUserPassword, setUserAdmin } from '../lib/api.ts'
import { dateTime } from '../lib/format.ts'
import './admin.css'

interface Reveal {
  username: string
  password: string
  isNew: boolean
}

export function AdminPage() {
  const { ready, authenticated, isAdmin, username: me } = useAuthContext()
  const [users, setUsers] = useState<AdminUser[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [reveal, setReveal] = useState<Reveal | null>(null)
  const [newName, setNewName] = useState('')
  const [newAdmin, setNewAdmin] = useState(false)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    try {
      setUsers(await fetchUsers())
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kunde inte hämta användare')
    }
  }, [])

  useEffect(() => {
    if (ready && authenticated && isAdmin) load()
  }, [ready, authenticated, isAdmin, load])

  if (ready && !authenticated) return <Navigate to="/login" replace />
  if (ready && !isAdmin) return <Navigate to="/" replace />

  const run = async (fn: () => Promise<void>) => {
    setBusy(true)
    setError(null)
    try {
      await fn()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Något gick fel')
    } finally {
      setBusy(false)
    }
  }

  const onAdd = (e: FormEvent) => {
    e.preventDefault()
    run(async () => {
      const res = await createUser(newName.trim(), newAdmin)
      setReveal({ username: res.username, password: res.password, isNew: true })
      setNewName('')
      setNewAdmin(false)
      await load()
    })
  }

  const onReset = (u: string) =>
    run(async () => {
      const res = await resetUserPassword(u)
      setReveal({ username: res.username, password: res.password, isNew: false })
    })

  const onDelete = (u: string) =>
    run(async () => {
      if (!confirm(`Ta bort användaren "${u}"?`)) return
      await deleteUser(u)
      if (reveal?.username === u) setReveal(null)
      await load()
    })

  const onToggleAdmin = (u: AdminUser) =>
    run(async () => {
      await setUserAdmin(u.username, !u.isAdmin)
      await load()
    })

  return (
    <div className="adminpage">
      <h1>Admin</h1>

      <PollSettingsCard />

      <h2 className="adminpage__h2">Användare</h2>
      <p className="muted">
        Konton för det inloggade läget. Den sista administratören kan inte tas bort.
      </p>

      {error && <div className="notice notice--error">{error}</div>}

      {reveal && (
        <div className="reveal card card__pad">
          <div className="card__title">
            {reveal.isNew ? 'Nytt konto skapat' : 'Nytt lösenord'} – {reveal.username}
          </div>
          <p className="muted">
            Lösenordet visas bara nu. Kopiera och lämna över på ett säkert sätt.
          </p>
          <div className="reveal__row">
            <code>{reveal.password}</code>
            <button
              type="button"
              className="btn"
              onClick={() => navigator.clipboard?.writeText(reveal.password)}
            >
              Kopiera
            </button>
            <button type="button" className="linkbtn" onClick={() => setReveal(null)}>
              Stäng
            </button>
          </div>
        </div>
      )}

      <form className="adduser card card__pad" onSubmit={onAdd}>
        <div className="card__title">Lägg till användare</div>
        <div className="adduser__row">
          <div className="field">
            <label htmlFor="nu">Användarnamn</label>
            <input
              id="nu"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="fornamn.efternamn"
              required
            />
          </div>
          <label className="adduser__admin">
            <input
              type="checkbox"
              checked={newAdmin}
              onChange={(e) => setNewAdmin(e.target.checked)}
            />
            Administratör
          </label>
          <button type="submit" className="btn btn--primary" disabled={busy}>
            Skapa
          </button>
        </div>
      </form>

      <div className="card card__pad">
        <table className="usertable">
          <thead>
            <tr>
              <th>Användarnamn</th>
              <th>Roll</th>
              <th>Skapad</th>
              <th>Senaste inloggning</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {users?.map((u) => {
              const isMe = u.username === me
              return (
                <tr key={u.username}>
                  <td>
                    {u.username}
                    {isMe && <span className="dim"> (du)</span>}
                    {u.fromSeed && (
                      <span className="dim" title="Kom från AUTH_USERS">
                        {' '}
                        · ursprungligt
                      </span>
                    )}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="rolltag"
                      onClick={() => onToggleAdmin(u)}
                      disabled={busy || isMe}
                      title={isMe ? 'Du kan inte ändra din egen roll' : 'Växla roll'}
                    >
                      {u.isAdmin ? 'Administratör' : 'Användare'}
                    </button>
                  </td>
                  <td className="dim">{dateTime(u.createdAt)}</td>
                  <td className="dim">{u.lastLoginAt ? dateTime(u.lastLoginAt) : 'Aldrig'}</td>
                  <td className="usertable__actions">
                    <button
                      type="button"
                      className="linkbtn"
                      onClick={() => onReset(u.username)}
                      disabled={busy}
                    >
                      Återställ lösenord
                    </button>
                    {!isMe && (
                      <button
                        type="button"
                        className="linkbtn linkbtn--danger"
                        onClick={() => onDelete(u.username)}
                        disabled={busy}
                      >
                        Ta bort
                      </button>
                    )}
                  </td>
                </tr>
              )
            })}
            {users?.length === 0 && (
              <tr>
                <td colSpan={5} className="dim">
                  Inga konton ännu.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
